'use client';

import React from 'react';

export type SimTimingMode = 'fully-random';
export type SimSpawnSide = 'random' | 'left' | 'right';

export interface SimulationSettings {
  enabled: boolean;
  timingMode: SimTimingMode;
  firstSpawnSide: SimSpawnSide;
  spawnSide: SimSpawnSide;
}

export const DEFAULT_SIMULATION_SETTINGS: SimulationSettings = {
  enabled: false,
  timingMode: 'fully-random',
  firstSpawnSide: 'random',
  spawnSide: 'random',
};

interface SimulationPanelProps {
  settings: SimulationSettings;
  onChange: (settings: SimulationSettings) => void;
  isOpen?: boolean;
  onOpenToggle?: () => void;
  isMinimized?: boolean;
  onMinimizeToggle?: () => void;
  onClose?: () => void;
}

const glassPanel: React.CSSProperties = {
  background: 'rgba(15, 15, 20, 0.72)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
};

const ACCENT = '#A78BFA';

export default function SimulationPanel({
  settings,
  onChange,
  isOpen = false,
  onOpenToggle,
  isMinimized,
  onMinimizeToggle,
  onClose,
}: SimulationPanelProps) {
  const update = <K extends keyof SimulationSettings>(key: K, value: SimulationSettings[K]) => {
    onChange({ ...settings, [key]: value });
  };

  const panelOpen = isOpen;

  const handleIconClick = () => {
    if (!panelOpen) {
      onOpenToggle?.();
    } else {
      onClose?.();
    }
  };

  // Icon-only state: panel is closed
  if (!panelOpen) {
    return (
      <button
        onClick={handleIconClick}
        className="flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-150"
        style={{
          background: settings.enabled ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${settings.enabled ? 'rgba(167,139,250,0.3)' : 'rgba(255,255,255,0.1)'}`,
          cursor: 'pointer',
          position: 'relative',
        }}
        title="Simulation Mode"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
          <circle cx="8" cy="12" r="4" stroke={settings.enabled ? ACCENT : 'rgba(255,255,255,0.7)'} strokeWidth="1.8"/>
          <circle cx="16" cy="12" r="4" stroke={settings.enabled ? ACCENT : 'rgba(255,255,255,0.7)'} strokeWidth="1.8"/>
          <circle cx="8" cy="12" r="1.5" fill={settings.enabled ? ACCENT : 'rgba(255,255,255,0.7)'}/>
          <circle cx="16" cy="12" r="1.5" fill={settings.enabled ? ACCENT : 'rgba(255,255,255,0.7)'}/>
        </svg>
        {settings.enabled && (
          <div
            className="absolute"
            style={{ top: '4px', right: '4px', width: '6px', height: '6px', borderRadius: '50%', background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }}
          />
        )}
      </button>
    );
  }

  // Open state — always fully expanded
  return (
    <div
      className="select-none"
      style={{
        width: '220px',
      }}
    >
      <div className="rounded-2xl overflow-hidden" style={glassPanel}>
        {/* Experimental bar */}
        <div
          className="w-full flex items-center justify-center py-0.5"
          style={{ background: 'rgba(220,38,38,0.15)', borderBottom: '1px solid rgba(220,38,38,0.25)' }}
        >
          <span
            className="text-xs font-bold tracking-widest uppercase"
            style={{ color: '#ef4444', fontSize: '9px', letterSpacing: '0.18em' }}
          >
            EXPERIMENTAL
          </span>
        </div>

        {/* Header */}
        <div className="w-full flex items-center gap-3 px-3 py-3" style={{ cursor: 'default' }}>
          <button
            onClick={handleIconClick}
            className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
            style={{ background: settings.enabled ? `${ACCENT}22` : 'rgba(167,139,250,0.12)', border: 'none', cursor: 'pointer', padding: 0 }}
            title="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="4" cy="7" r="2.5" stroke={settings.enabled ? ACCENT : 'rgba(167,139,250,0.7)'} strokeWidth="1.4"/>
              <circle cx="10" cy="7" r="2.5" stroke={settings.enabled ? ACCENT : 'rgba(167,139,250,0.7)'} strokeWidth="1.4"/>
              <path d="M6.5 7h1" stroke={settings.enabled ? ACCENT : 'rgba(167,139,250,0.7)'} strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </button>
          <span
            className="text-xs font-semibold tracking-widest uppercase flex-1 text-left"
            style={{ color: 'rgba(232,232,240,0.9)', fontFamily: 'var(--font-sans)', letterSpacing: '0.12em' }}
          >
            Simulation
          </span>
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center justify-center w-5 h-5 rounded-md hover:bg-white/10 transition-colors"
              style={{ border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.3)', background: 'transparent' }}
              title="Close"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-3 pb-3 space-y-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>

          {/* Simulation Mode toggle */}
          <div className="pt-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                Simulation Mode
              </span>
              <button
                onClick={() => update('enabled', !settings.enabled)}
                className="relative flex-shrink-0 transition-all duration-200"
                style={{
                  width: '36px', height: '20px', borderRadius: '10px',
                  background: settings.enabled ? ACCENT : 'rgba(255,255,255,0.12)',
                  border: `1px solid ${settings.enabled ? ACCENT : 'rgba(255,255,255,0.15)'}`,
                  cursor: 'pointer', padding: 0,
                }}
              >
                <span
                  className="absolute top-0.5 transition-all duration-200"
                  style={{
                    left: settings.enabled ? '17px' : '2px',
                    width: '14px', height: '14px', borderRadius: '50%',
                    background: settings.enabled ? '#fff' : 'rgba(255,255,255,0.5)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  }}
                />
              </button>
            </div>
            <p className="mt-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '9px', lineHeight: 1.5 }}>
              {settings.enabled ? 'Second target active — split attention required' : 'Off — single target gameplay (default)'}
            </p>
          </div>

          {/* Timing Mode — Fully Random only */}
          <div style={{ opacity: settings.enabled ? 1 : 0.4, transition: 'opacity 0.2s' }}>
            <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
              Timing Mode
            </span>
            <div
              className="px-2.5 py-2 rounded-lg"
              style={{
                background: `${ACCENT}18`,
                border: `1px solid ${ACCENT}55`,
              }}
            >
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 rounded-full" style={{ width: '7px', height: '7px', background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }} />
                <span style={{ color: ACCENT, fontSize: '10px', fontWeight: 700, fontFamily: 'var(--font-sans)' }}>
                  Fully Random
                </span>
              </div>
              <p className="mt-0.5 ml-3.5" style={{ color: 'rgba(255,255,255,0.28)', fontSize: '8.5px', lineHeight: 1.4 }}>
                Randomized delay between targets each round
              </p>
            </div>
          </div>

          {/* First Spawn Side */}
          <div style={{ opacity: settings.enabled ? 1 : 0.4, transition: 'opacity 0.2s' }}>
            <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
              First Spawn Side
            </span>
            <div className="grid grid-cols-3 gap-1">
              {([
                { id: 'random' as SimSpawnSide, label: 'Random' },
                { id: 'left' as SimSpawnSide, label: 'Left' },
                { id: 'right' as SimSpawnSide, label: 'Right' },
              ] as { id: SimSpawnSide; label: string }[]).map((opt) => {
                const isActive = settings.firstSpawnSide === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => settings.enabled && update('firstSpawnSide', opt.id)}
                    className="px-1 py-1.5 rounded-lg transition-all duration-150 text-center"
                    style={{
                      background: isActive ? `${ACCENT}18` : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isActive ? `${ACCENT}55` : 'rgba(255,255,255,0.08)'}`,
                      cursor: settings.enabled ? 'pointer' : 'default',
                      fontSize: '10px', fontFamily: 'var(--font-sans)',
                      color: isActive ? ACCENT : 'rgba(255,255,255,0.5)',
                      fontWeight: isActive ? 700 : 500, letterSpacing: '0.04em',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Secondary Spawn Side */}
          <div style={{ opacity: settings.enabled ? 1 : 0.4, transition: 'opacity 0.2s' }}>
            <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
              Secondary Spawn Side
            </span>
            <div className="grid grid-cols-3 gap-1">
              {([
                { id: 'random' as SimSpawnSide, label: 'Random' },
                { id: 'left' as SimSpawnSide, label: 'Left' },
                { id: 'right' as SimSpawnSide, label: 'Right' },
              ] as { id: SimSpawnSide; label: string }[]).map((opt) => {
                const isActive = settings.spawnSide === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => settings.enabled && update('spawnSide', opt.id)}
                    className="px-1 py-1.5 rounded-lg transition-all duration-150 text-center"
                    style={{
                      background: isActive ? `${ACCENT}18` : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isActive ? `${ACCENT}55` : 'rgba(255,255,255,0.08)'}`,
                      cursor: settings.enabled ? 'pointer' : 'default',
                      fontSize: '10px', fontFamily: 'var(--font-sans)',
                      color: isActive ? ACCENT : 'rgba(255,255,255,0.5)',
                      fontWeight: isActive ? 700 : 500, letterSpacing: '0.04em',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
